"use strict";

const PostConstructionProjectG2Filters = function () {
  const searchFields = ["projectIdentifier", "projectName", "streetAddress"];
  const searchDbFields = ["project_identifier", "project_name", "street_address"];

  var filterResourceDataHandler = async function (
    unfilteredData,
    filters,
    loadingScreen,
    smartFilters,
  ) {
    if (onlineFilterIsSet(filters)) {
      try {
        return await ResourceFilters.filterOnline(
          unfilteredData,
          loadingScreen,
          PostConstructionProjectGeoServerLayer.getLayerData,
        );
      } catch (e) {
        ApiError.assertIs(e);
      }
    }

    return filterOffline(unfilteredData, filters, smartFilters);
  };

  var onlineFilterIsSet = function (filters) {
    if (filters.toDoFilters) {
      return ResourceFilters.toDoSpatialFiltersAreSet(filters.toDoFilters);
    } else {
      return ResourceFilters.spatialFiltersAreSet(filters) || isOnlineDateFilterSet(filters);
    }
  };

  var isOnlineDateFilterSet = function (filters) {
    return isOnlineDateType(filters.dateType) && (filters.dateFrom || filters.dateTo);
  };

  var isOnlineDateType = function (dateType) {
    return ["routine", "repair", "clean"].includes(dateType);
  };

  var filterOffline = function (unfilteredData, filters, smartFilters) {
    return smartFilters ? smartFilters.filter(unfilteredData, filters) : unfilteredData;
  };

  var passFeatureFlaggedCompletedDateFilter = function (datum, filters) {
    return ResourceFilters.passIsoDateFilter(
      datum.projectEndDate,
      filters.completed?.from,
      filters.completed?.to,
    );
  };

  var passNoticeSentDateFilter = function (datum, filters) {
    if (!ResourceFilters.isDateFilterSet(filters.noticeSentDate)) {
      return true;
    }

    return datum.noticeSentDates.some((date) => {
      return ResourceFilters.passDateFilter(
        date,
        filters.noticeSentDate?.from,
        filters.noticeSentDate?.to,
      );
    });
  };

  var addGeoServerFilterQuery = function (queryStrings, filters) {
    if (Tree.get("dataView") === "lid-runoff") {
      const defaultPhases = ["completed", "certified"];
      queryStrings.push(GeoServerFilterFunctions.getInArrayQuery("project_phase", defaultPhases));
    } else {
      if (filters.status?.length !== undefined) {
        const phases = getFilterPhases(filters);
        queryStrings.push(GeoServerFilterFunctions.getInArrayQuery("project_phase", phases));
      }

      GeoServerFilterFunctions.addFilterToGeoServerQuery(
        queryStrings,
        filters,
        "lidOwnership",
        "implementer_type",
      );

      GeoServerFilterFunctions.addFilterToGeoServerQuery(
        queryStrings,
        filters,
        "maintainerType",
        "maintainer_type",
      );

      GeoServerFilterFunctions.addFilterToGeoServerQuery(
        queryStrings,
        filters,
        "performanceTier",
        "highest_tier_number",
      );

      if (
        filters.alternativeCompliance &&
        !SmartFilterRegistry.getSummary()?.areAllCheckboxesChecked("alternativeCompliance")
      ) {
        queryStrings = GeoServerFilterFunctions.yesNoNullGeoServerFilter(
          filters,
          "alternativeCompliance",
          queryStrings,
          "alternative_compliance",
        );
      }

      if (
        filters.initialMaintenanceNotice &&
        !SmartFilterRegistry.getSummary()?.areAllCheckboxesChecked("initialMaintenanceNotice")
      ) {
        queryStrings = GeoServerFilterFunctions.yesNoNullGeoServerFilter(
          filters,
          "initialMaintenanceNotice",
          queryStrings,
          "initial_maintenance_notice",
        );
      }

      if (
        filters.initialRoutineInspection &&
        !SmartFilterRegistry.getSummary()?.areAllCheckboxesChecked("initialRoutineInspection")
      ) {
        queryStrings = GeoServerFilterFunctions.yesNoNullGeoServerFilter(
          filters,
          "initialRoutineInspection",
          queryStrings,
          "initial_routine_inspection",
        );
      }

      if (
        filters.openEnforcements &&
        !SmartFilterRegistry.getSummary()?.areAllCheckboxesChecked("openEnforcements")
      ) {
        queryStrings = GeoServerFilterFunctions.yesNoNullGeoServerFilter(
          filters,
          "openEnforcements",
          queryStrings,
          "open_enforcements",
        );
      }

      if (filters.searchString) {
        queryStrings.push(
          GeoServerFilterFunctions.getSearchQuery(searchDbFields, filters.searchString),
        );
      }
      GeoServerFilterFunctions.addGeographicalFocusFiltersToQuery(filters, queryStrings, [
        { key: "drainsToRw", field: "drains_to_rw" },
        { key: "drainsToC", field: "drains_to_c" },
        { key: "catchmentName", field: "catchment_name" },
      ]);

      if (filters.dateTypeLidProject === "completed") {
        GeoServerFilterFunctions.addDateQuery(
          queryStrings,
          "project_end_date",
          filters.completed?.from,
          filters.completed?.to,
        );
      }
    }
  };

  var getFilterPhases = function (filters) {
    let phases = filters.status;

    if (filters.conditionPhaseToggle === "condition") {
      phases = phases.filter((phase) => ["completed", "certified"].includes(phase));
    }

    return phases;
  };

  var addGeoServerViewParams = function (params, filters) {
    ResourceFilters.addDateParam(
      params,
      "noticeSent",
      filters.noticeSentDate?.from,
      filters.noticeSentDate?.to,
    );
  };

  var getFilterConfigs = function () {
    return {
      status: "phase",
      lidOwnership: "implementerType",
      maintainerType: "maintainerType",
      performanceTier: "performanceTier",
      alternativeCompliance: "alternativeCompliance",
      initialMaintenanceNotice: "initialMaintenanceNotice",
      initialRoutineInspection: "initialRoutineInspection",
      drainsToRw: "drains_to_rw",
      drainsToC: "drains_to_c",
      catchmentName: "catchment_name",
      highwayName: "highway_name",
      subGroups: "groupId",
      openEnforcements: "openEnforcements",
      ...ResourceFilters.getSmartToDoFilters(),

      otherFilters: (datum, filters) => {
        return (
          ResourceFilters.passSearchStringFilter(datum, filters, searchFields) &&
          passFeatureFlaggedCompletedDateFilter(datum, filters) &&
          passNoticeSentDateFilter(datum, filters)
        );
      },
    };
  };

  return {
    filterResourceDataHandler,
    addGeoServerFilterQuery,
    addGeoServerViewParams,
    passFeatureFlaggedCompletedDateFilter,
    getFilterConfigs,
  };
};

module.exports = PostConstructionProjectG2Filters();
const ApiError = require("../errors/apiError");
const SmartFilterRegistry = require("../filters/smartFilterRegistry");
const ResourceFilters = require("../filters/resourceFilters");
const GeoServerFilterFunctions = require("../mapFunctions/geoServerFilterFunctions");
const Tree = require("../tree");
const PostConstructionProjectGeoServerLayer = require("./postConstructionProjectGeoServerLayer");
